import React, { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  ResponsiveContainer,
} from 'recharts';
import { getEvolutionTransactionClient } from '../service/api/TransactionService';
import useSelectedDomain from '../service/Hooks/useSelectedDomain';
import { useUserData } from '../service/api/useUserData';

const TransactionClientEvolution = () => {
  const [data, setData] = useState([]);
  const selectedDomain = useSelectedDomain();
  const { getUserInfo } = useUserData();
  const [creatorId, setCreatorId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const moisNoms = [
    '', 'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  const moisCouleurs = [
    '#FFFFFF', '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0',
    '#9966FF', '#FF9F40', '#8884d8', '#00C851', '#DD4814',
    '#673AB7', '#26A69A', '#FF5733',
  ];

  useEffect(() => {
    async function fetchUserData() {
      setLoading(true);
      try {
        const userInfo = await getUserInfo();
        if (userInfo) {
          setCreatorId(userInfo.accountId);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    fetchUserData();
  }, [getUserInfo]);

  useEffect(() => {
    const fetchData = async () => {
      if (!selectedDomain?.id || !creatorId) return;
      setLoading(true);
      try {
        const responseData = await getEvolutionTransactionClient(creatorId, selectedDomain.id);
        const moisComplets = Array.from({ length: 12 }, (_, i) => ({ month: i + 1, volume: 0 }));

        // Fusionner avec les données API
        responseData.forEach((item) => {
          const index = moisComplets.findIndex((m) => m.month === item.month);
          if (index !== -1) moisComplets[index].volume = item.volume;
        });

        setData(moisComplets);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedDomain, creatorId]);

  const renderCustomTick = (props) => {
    const { x, y, payload } = props;
    const month = payload.value;
    const name = moisNoms[month] || '';
    const fillColor = moisCouleurs[month] || '#000';
    return (
      <text x={x} y={y + 12} textAnchor="middle" fontSize={12} fill={fillColor}>
        {name}
      </text>
    );
  };

  if (loading) return <p>Chargement...</p>;
  if (error) return <p>Erreur : {error}</p>;

  return (
    <div className="m-5" style={{ width: '100%', height: 500 }}>
      <h2 style={{ textAlign: 'center', marginBottom: 20 }}>
        Évolution Mensuelle des Transactions
      </h2>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={data}
          margin={{ top: 30, right: 100, bottom: 40, left: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#ccc" />
          <XAxis
            dataKey="month"
            tick={renderCustomTick}
            tickLine={false}
            interval={0}
            domain={[1, 12]}
            type="number"
            label={{
              value: 'Mois',
              position: 'insideBottom',
              offset: -20,
              fontSize: 12,
            }}
          />
          <YAxis
            tickLine={false}
            fontSize={12}
            label={{
              value: 'Volume',
              angle: -90,
              position: 'insideLeft',
              offset: 0,
              fontSize: 12,
            }}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: '#f5f5f5',
              border: '1px solid #ccc',
              fontSize: 12,
            }}
          />
          <Legend verticalAlign="top" height={40} wrapperStyle={{ fontSize: 12 }} />
          <Line
            type="monotone"
            dataKey="volume"
            stroke="#8884d8"
            strokeWidth={3}
            activeDot={{ r: 8 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default TransactionClientEvolution;
